import { useCallback, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  ClientSettingsPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { getAllPreferences, uploadLogo } from '../../../services/queries/MaggiePreferencesQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { getEnvironment } from '../../../util/environment';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { LoadingId } from '../../../types';

export function getLogoUrl() {
  const client = getEnvironment();

  return (
    PreferencesServiceV3.get().getPreference<ClientSettingsPreferences>(
      PreferenceKey.clientSettings,
      PreferenceScope.ORGANIZATION
    )?.logoUrl || `https://foresight-public-files.s3.amazonaws.com/base-logo-${client}.png`
  );
}

export function useClientSettings() {
  const { pushErrorToast } = useToastMessageState();
  const [loading, setLoading] = useState(false);
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();
  const [rerender, setRerender] = useState(0);
  const [settings, setSettings] = useState<ClientSettingsPreferences>(() => {
    const currentSettings = PreferencesServiceV3.get().getPreference<ClientSettingsPreferences>(
      PreferenceKey.clientSettings,
      PreferenceScope.ORGANIZATION
    );
    return {
      logoUrl: getLogoUrl(),
      name: currentSettings?.name ?? getEnvironment(),
      displayName: currentSettings?.displayName ?? '',
      profileLogoUrl: currentSettings?.profileLogoUrl ?? '',
    };
  });
  const onUpdateLogo = useCallback(
    async (file: File) => {
      setLoading(true);
      startLoading(LoadingId.updateClientLogo);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const { logoUrl } = await uploadLogo(formData);
        setRerender((prev) => prev + 1);
        await invalidatePreferences();
        setSettings((prev) => ({
          ...prev,
          logoUrl: getLogoUrl(), // getLogoUrl will return pref if set, otherwise default
        }));
        return logoUrl;
      } catch (error) {
        console.error(error);
        pushErrorToast({ message: getErrorMessage(error, 'Failed to upload logo') });
      } finally {
        stopLoading(LoadingId.updateClientLogo);
        setLoading(false);
      }
    },
    [pushErrorToast, startLoading, stopLoading]
  );

  const onSaveSettings = useCallback(
    async (values: Pick<ClientSettingsPreferences, 'displayName' | 'profileLogoUrl'>) => {
      setLoading(true);
      try {
        const current =
          PreferencesServiceV3.get().getPreference<ClientSettingsPreferences>(
            PreferenceKey.clientSettings,
            PreferenceScope.ORGANIZATION
          ) ?? {};
        const updated = await PreferencesServiceV3.get().setPreference<ClientSettingsPreferences>(
          PreferenceKey.clientSettings,
          { ...current, ...values },
          PreferenceScope.ORGANIZATION
        );
        const nextValue = (updated?.value as ClientSettingsPreferences) ?? { ...current, ...values };
        setSettings((prev) => ({
          ...prev,
          displayName: nextValue.displayName ?? '',
          profileLogoUrl: nextValue.profileLogoUrl ?? '',
        }));
      } catch (error) {
        console.error(error);
        pushErrorToast({ message: getErrorMessage(error, 'Failed to save settings') });
      } finally {
        setLoading(false);
      }
    },
    [pushErrorToast]
  );

  return { settings, onUpdateLogo, onSaveSettings, rerender, loading };
}

/* The preference service currently does not support updating a single preference without calling the api,
 * so we invalidate all;
 * If the response gets slower, we will need to change this
 */
async function invalidatePreferences() {
  try {
    const prefs = await getAllPreferences();
    PreferencesServiceV3.destroyService();
    PreferencesServiceV3.initService(prefs);
  } catch (error) {
    console.error(error);
  }
}
